/**
 * useSkillMatrixHotkeys.ts
 * Window-level keyboard handler for the Skill Matrix range-selection flow.
 *
 * Key bindings:
 *   1 / 2 / 3 / 4 / 5  — set all selected cells to that skill level
 *   Backspace / Delete  — clear (null) all selected cells
 *   Escape              — clear the visual selection (keep cell data intact)
 *   ArrowUp/Down/Left/Right   — move focus one cell in that direction
 *   Shift+Arrow               — extend selection in that direction
 *   Enter / F2          — open the single-cell popover for the focused cell
 *   Tab / Shift+Tab     — move focus right / left (no selection extension)
 *   Ctrl+A / Cmd+A      — select all currently-visible cells
 *
 * Ctrl+Z / Ctrl+Shift+Z are handled by useUndoRedoHotkeys — not duplicated here.
 *
 * The hook skips all hotkeys when the keyboard focus is inside an INPUT,
 * TEXTAREA, or contentEditable element so that searching/filtering still works.
 */

import { useEffect, useCallback } from 'react';
import { useSkillMatrixStore } from '../../../../state/skillMatrixStore';
import {
  resolveSelectedCoords,
  buildSelectionSet,
  type CellCoord,
} from './selectionMath';
import type { BatchCellUpdate, SkillMatrixCell, SkillUser, MatrixSkill } from '../../../../services/scheduleSkillMatrix';

interface UseSkillMatrixHotkeysOptions {
  /** Visible (filtered) users — defines valid row coordinates. */
  users: SkillUser[];
  /** Visible task types — defines valid column coordinates. */
  skills: MatrixSkill[];
  /** Called when the user presses Enter/F2 on the focused cell. */
  onOpenCellEditor: (cell: SkillMatrixCell, anchorEl: HTMLElement) => void;
  /** Called on batch-apply errors (e.g. network failure). */
  onError?: (message: string) => void;
  /**
   * When false, all grid hotkeys (level 1–5, Delete, arrow navigation, popover)
   * are disabled. The Skill Matrix is view-only until the user turns on Edit
   * mode, so keyboard edits must not fire either (BUG-2026-0098). Defaults to true.
   */
  enabled?: boolean;
}

/** Returns true when focus is inside a text-entry element. */
function isTypingFocus(): boolean {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  return (
    el.tagName === 'INPUT' ||
    el.tagName === 'TEXTAREA' ||
    el.isContentEditable
  );
}

export function useSkillMatrixHotkeys({
  users,
  skills,
  onOpenCellEditor,
  onError,
  enabled = true,
}: UseSkillMatrixHotkeysOptions): void {
  const store = useSkillMatrixStore;

  /** Apply a numeric level (or null) to every cell in the current selection. */
  const applyLevelToSelection = useCallback(
    async (level: number | null) => {
      const state = store.getState();
      const { selection, cells, applyAndPersist } = state;
      const selected = resolveSelectedCoords(selection.anchor, selection.focus, users, skills);
      if (selected.length === 0) return;

      // Build batch payload and fallback cells.
      const batchPayload: BatchCellUpdate[] = [];
      const fallbacks: SkillMatrixCell[] = [];

      for (const coord of selected) {
        batchPayload.push({ UserKey: coord.userKey, SkillKey: coord.skillKey, Level: level });

        const existing = cells.find(
          (c) => c.UserKey === coord.userKey && c.SkillKey === coord.skillKey,
        );
        if (existing) {
          fallbacks.push(existing);
        } else {
          const user = users.find((u) => u.AppUserKey === coord.userKey);
          const tt = skills.find((t) => t.SkillKey === coord.skillKey);
          if (user && tt) {
            fallbacks.push({
              SkillMatrixKey: null,
              UserKey: coord.userKey,
              UserDisplayName: user.DisplayName,
              UserEmail: user.Email,
              JobClass: user.JobClass,
              WorkGroup: user.WorkGroup,
              Tier: user.Tier,
              SkillKey: coord.skillKey,
              SkillName: tt.Name,
              SkillCategory: tt.Category,
              Level: null,
            });
          }
        }
      }

      try {
        await applyAndPersist(batchPayload, fallbacks);
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Failed to update skill levels';
        onError?.(msg);
      }
    },
    [store, users, skills, onError],
  );

  /** Move focus by delta in row/col space, optionally extending the selection. */
  const moveFocus = useCallback(
    (rowDelta: number, colDelta: number, extend: boolean) => {
      const { selection, setFocus, extendSelection } = store.getState();
      const current = selection.focus ?? selection.anchor;
      if (!current) {
        // No selection — focus the first cell if available.
        if (users.length > 0 && skills.length > 0) {
          setFocus({ userKey: users[0].AppUserKey, skillKey: skills[0].SkillKey });
        }
        return;
      }

      const rowIdx = users.findIndex((u) => u.AppUserKey === current.userKey);
      const colIdx = skills.findIndex((tt) => tt.SkillKey === current.skillKey);
      if (rowIdx === -1 || colIdx === -1) return;

      const newRow = Math.max(0, Math.min(users.length - 1, rowIdx + rowDelta));
      const newCol = Math.max(0, Math.min(skills.length - 1, colIdx + colDelta));
      const newCoord: CellCoord = {
        userKey: users[newRow].AppUserKey,
        skillKey: skills[newCol].SkillKey,
      };

      if (extend) {
        extendSelection(newCoord);
      } else {
        setFocus(newCoord);
      }
    },
    [store, users, skills],
  );

  /** Find the DOM cell element for a given coord (used by Enter/F2). */
  const findCellElement = useCallback((coord: CellCoord): HTMLElement | null => {
    return document.querySelector<HTMLElement>(
      `[data-user-key="${CSS.escape(coord.userKey)}"][data-task-type-key="${CSS.escape(coord.skillKey)}"]`,
    );
  }, []);

  useEffect(() => {
    // View-only mode: do not register the grid keyboard handler at all.
    if (!enabled) return;
    const onKeyDown = (e: KeyboardEvent) => {
      // Never intercept when the user is typing in a form field.
      if (isTypingFocus()) return;

      const isMod = e.ctrlKey || e.metaKey;
      const { selection, cells, setFocus, clearSelection } = store.getState();
      const selectionSet = buildSelectionSet(
        resolveSelectedCoords(selection.anchor, selection.focus, users, skills),
      );
      const hasSelection = selectionSet.size > 0;

      switch (e.key) {
        // ── Level assignment (1–5 + 9 Approver, RBAC Phase C) ───────────────
        case '1':
        case '2':
        case '3':
        case '4':
        case '5':
        case '9':
          if (!isMod && hasSelection) {
            e.preventDefault();
            void applyLevelToSelection(Number(e.key));
          }
          break;

        // ── Clear cells (keep visual selection) ─────────────────────────────
        case 'Backspace':
        case 'Delete':
          if (hasSelection) {
            e.preventDefault();
            void applyLevelToSelection(null);
          }
          break;

        // ── Clear visual selection ───────────────────────────────────────────
        case 'Escape':
          if (hasSelection || selection.focus) {
            e.preventDefault();
            clearSelection();
          }
          break;

        // ── Arrow navigation ─────────────────────────────────────────────────
        case 'ArrowUp':
          e.preventDefault();
          moveFocus(-1, 0, e.shiftKey);
          break;
        case 'ArrowDown':
          e.preventDefault();
          moveFocus(1, 0, e.shiftKey);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          moveFocus(0, -1, e.shiftKey);
          break;
        case 'ArrowRight':
          e.preventDefault();
          moveFocus(0, 1, e.shiftKey);
          break;

        // ── Tab navigation (no selection extension) ──────────────────────────
        case 'Tab': {
          if (!selection.focus) break;
          e.preventDefault();
          moveFocus(0, e.shiftKey ? -1 : 1, false);
          break;
        }

        // ── Open single-cell popover ─────────────────────────────────────────
        case 'Enter':
        case 'F2': {
          if (!selection.focus) break;
          e.preventDefault();
          const focusCoord = selection.focus;
          const domEl = findCellElement(focusCoord);
          if (!domEl) break;
          const existingCell = cells.find(
            (c) => c.UserKey === focusCoord.userKey && c.SkillKey === focusCoord.skillKey,
          );
          const user = users.find((u) => u.AppUserKey === focusCoord.userKey);
          const tt = skills.find((t) => t.SkillKey === focusCoord.skillKey);
          if (!user || !tt) break;
          const syntheticCell: SkillMatrixCell = existingCell ?? {
            SkillMatrixKey: null,
            UserKey: focusCoord.userKey,
            UserDisplayName: user.DisplayName,
            UserEmail: user.Email,
            JobClass: user.JobClass,
            WorkGroup: user.WorkGroup,
            Tier: user.Tier,
            SkillKey: focusCoord.skillKey,
            SkillName: tt.Name,
            SkillCategory: tt.Category,
            Level: null,
          };
          onOpenCellEditor(syntheticCell, domEl);
          break;
        }

        // ── Select all visible cells ─────────────────────────────────────────
        case 'a':
        case 'A':
          if (isMod && users.length > 0 && skills.length > 0) {
            e.preventDefault();
            // Anchor = top-left, focus = bottom-right.
            setFocus({ userKey: users[0].AppUserKey, skillKey: skills[0].SkillKey });
            // Use extendSelection after setFocus to land focus at bottom-right.
            const { extendSelection } = store.getState();
            extendSelection({
              userKey: users[users.length - 1].AppUserKey,
              skillKey: skills[skills.length - 1].SkillKey,
            });
          }
          break;

        default:
          break;
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    enabled,
    store,
    users,
    skills,
    applyLevelToSelection,
    moveFocus,
    findCellElement,
    onOpenCellEditor,
  ]);
}
